import _get from 'lodash/get';
import { getCurrentTimeInSeconds, timeTitleToSeconds } from '@bedrock-rdk/utils';
import { findCurrentLiveItem, getMetadataItemsFromLayout, getTimeToRequestNewMetadataInSeconds } from './live.utils';
import { PARENTAL_LOCK_STATUS } from '../user/user.actions';
import {
  INITIALIZED,
  MANIFEST_LOADED,
  CAN_PLAY,
  PLAYING,
  PAUSED,
  WAITING,
  TIME_UPDATE,
  TRACKS_CHANGED,
  SUBTITLES_UPDATED,
  ENDED,
  RESET,
  LOCKED,
  AD_PROVIDER_LOADED,
  AD_BREAK_STARTED,
  AD_BREAK_ENDED,
  AD_PLAYING,
  AD_PAUSED,
  AD_ENDED,
  AD_ERROR,
  AD_JINGLE_STARTED,
  APPEND_NEW_METADATA_ITEMS,
  SIDE_GLASS_PAGE_REQUESTED,
  SIDE_GLASS_PAGE_LOADED,
  SIDE_GLASS_PAGE_FAILED,
  SET_CURRENT_METADATA_ITEM,
  PLAYBACK_STATE_ENDED,
  PLAYBACK_STATE_IDLE,
  PLAYBACK_STATE_PAUSED,
  PLAYBACK_STATE_PLAYING,
  RESET_LAST_VIEWED_VIDEO_DATA,
  SET_AUDIO_TRACK,
  SET_SUBTITLE_TRACK,
  TRACK_PREFERENCES_CHANGED,
  SET_PAUSE_AD,
  PAUSE_AD_STATE,
  SET_PAUSE_AD_STATE,
  SET_LBANNER_STATE,
  UPDATE_METADATA,
  CAN_PLAY_THROUGH,
  MIN_SECONDS_TO_TRACK_PROGRESS,
  SET_PLAYER_ERROR,
  SWITCH_TO_NEXT_SOURCE,
  AD_STARTED,
  AD_TIME_UPDATE,
  AD_MARKERS_LIST,
  LOAD_START,
  PLAYER_MODE,
  AD_BREAK_MID_ROLL,
  AD_JINGLE_CLIP_PLAYING,
  AD_JINGLE_POSITION,
  SHOW_PROGRAM_JINGLE,
} from './player.constants';
import {
  selectNextPlayableSource,
  prepareSideGlassBlock,
  findVideoItemProgramImageId,
  findJingleImageId,
  findAdJingleImageId,
  findSideGlassBlock,
  findForcedSubtitles,
  findMatchingAudioTrack,
  findMatchingSubtitlesTrack,
  audioTrackToPreference,
  subtitlesTrackToPreference,
  parseTrack,
  arrangeSources,
  removeParentalLock,
} from './player.utils';
import { getPercentage } from '../../modules/tornado/progress/progress.utils';
import { PAGE_STATE, PLACEHOLDER } from '../../modules/tornado/utils/virtualization';
import { CHAPTER_START_OFFSET_IN_SECONDS } from './video.constants';
import { POST_AD_JINGLE_DELAY } from '../../modules/player/player.constants';

export const initialState = {
  isLive: false,
  mode: PLAYER_MODE.VIDEO_JINGLE,
  isStarted: false,
  canPlay: false,
  isLoadingContent: false,
  isBuffering: false,
  playbackState: PLAYBACK_STATE_IDLE,
  adPlaybackState: PLAYBACK_STATE_IDLE,
  videoAds: undefined,
  adBreakDuration: 0,
  adElapsedTime: 0,
  adSkipOffset: undefined,
  adBreakElapsedTime: 0,
  adMarkers: undefined,
  isAdProviderReady: false,
  isAdError: false,
  pauseAd: null,
  pauseAdState: PAUSE_AD_STATE.IDLE,
  displayedAdBreakType: null,
  durationInSeconds: 0,
  startTimeInSeconds: 0,
  elapsedTimeInSeconds: undefined,
  liveEdgeInSeconds: 0,
  timeUpdateTicks: 0,
  isOnLive: false,
  shouldHideVideoLoader: false,
  currentSourceIndex: null,
  sideGlass: null,
  programImageId: '',
  jingleImageId: '',
  sources: [],
  contentType: '',
  preferences: null,
  originalLanguage: '',
  selectedSubtitlesTrack: null,
  selectedAudioTrack: null,
  audioTracks: [],
  subtitleTracks: [],
  subtitleCuePoint: null,
  chapter: null,
  chapters: [],
  isLocked: false,
  shouldLiftParentalLock: false,
  metadataItems: [],
  currentMetadataItemIndex: 0,
  lastMetadataItemIndex: 0,
  isNextLiveLayoutLoading: false,
  timeToLoadNewLayout: 0,
  liveStartOverFeature: false,
  lastViewedClipId: '',
  lastViewedClipProgress: 0,
  lastError: null,
};

export default (state = initialState, { type, ...data }) => {
  switch (type) {
    case LOCKED: {
      return {
        ...initialState,
        isLocked: true,
      };
    }
    case PARENTAL_LOCK_STATUS: {
      const { isUnlocked } = data;

      if (!isUnlocked) {
        return state;
      }

      return {
        ...state,
        shouldLiftParentalLock: true,
      };
    }
    case INITIALIZED: {
      const { layout, isLive, entityId, entityType } = data;

      const items = isLive ? getMetadataItemsFromLayout(layout) : [data.item];
      const item = (isLive && findCurrentLiveItem(layout, getCurrentTimeInSeconds())) || data.item;
      let currentMetadataItemIndex = items.indexOf(item);
      if (currentMetadataItemIndex < 0) {
        currentMetadataItemIndex = 0;
      }

      const startTimeInSeconds = isLive ? _get(item, 'video.progress.live.startTimestamp', 0) : 0;
      const endTimeInSeconds = isLive
        ? _get(item, 'video.progress.live.endTimestamp', 0)
        : timeTitleToSeconds(_get(item, 'video.progress.endTitle'));
      const durationInSeconds = endTimeInSeconds - startTimeInSeconds;
      const elapsedTimeInSeconds = isLive ? undefined : _get(item, 'video.progress.tcResume', 0);
      const sources = arrangeSources(item).map(source => {
        source.isLive = isLive;
        source.shouldAutoPlay = isLive || entityType === 'radio';
        source.startTime = endTimeInSeconds - elapsedTimeInSeconds >= 5 ? elapsedTimeInSeconds : undefined;
        source.type = entityType;

        return source;
      });

      const videoAdsProvider = _get(item, 'advertising.providers.video');
      const videoAds = videoAdsProvider ? _get(item, `advertising.${videoAdsProvider}`) : undefined;

      // NOTE: Unocmment to test VAST pause ads
      // item.advertising.providers = {
      //   pausevast: 'fake',
      // };
      // item.advertising.fake = {
      //   host: 'http://localhost:3000/static/assets/ads/VAST.pause.xml',
      //   hostTargetingSeparator: '?',
      //   targetingKeysSeparator: '&',
      //   targeting: {
      //     test: '123',
      //   },
      //   brParams: {
      //     closeActionEnabled: true,
      //     closeActionDelay: 5,
      //     delayBeforeDisplay: 2,
      //     viewableImpressionDelay: 2,
      //     maxDisplayDuration: 15,
      //   },
      // };

      const lastMetadataItemIndex = items.length - 1;
      const timeToLoadNewLayout = getTimeToRequestNewMetadataInSeconds(durationInSeconds);
      const liveStartOverFeature = isLive ? _get(item, 'video.features.startOver', false) : false;
      const originalLanguage = _get(item, 'video.originalLanguage') || '';
      const result = {
        ...initialState,
        sources,
        preferences: _get(item, 'video.features.preferences') || {},
        originalLanguage,
        lastMetadataItemIndex,
        timeToLoadNewLayout,
        // TODO: find a most appropriate source for current platform(basing on hardware DRM support, for examples)
        // TODO: repeat it starting from a last-try index in case of error while this functions returns values greater than -1
        currentSourceIndex: selectNextPlayableSource({ sources }),
        programImageId: findVideoItemProgramImageId(item),
        jingleImageId: findJingleImageId(item),
        adJingleImageId: findAdJingleImageId(item),
        sideGlass: prepareSideGlassBlock(findSideGlassBlock(layout)),
        durationInSeconds,
        startTimeInSeconds,
        elapsedTimeInSeconds,
        chapters: (_get(item, 'video.chapters') || []).map(({ tcEnd, tcStart, type }) => ({
          tcStart: Math.min(tcStart + CHAPTER_START_OFFSET_IN_SECONDS, tcEnd),
          tcEnd,
          type,
        })),
        metadataItems: state.shouldLiftParentalLock ? removeParentalLock(items) : items,
        shouldLiftParentalLock: false,
        currentMetadataItemIndex,
        isLive,
        liveStartOverFeature,
        lastViewedClipId: entityId,
        contentType: entityType,
        videoAds,
      };

      return result;
    }
    case LOAD_START:
      return {
        ...state,
        chapter: null,
        // App-owned: (re)loading content shows the video jingle surface.
        mode: PLAYER_MODE.VIDEO_JINGLE,
        canPlay: false,
        isLoadingContent: true,
      };
    case SHOW_PROGRAM_JINGLE:
      // App-owned: advance from the video jingle only; never override an ad/content surface.
      return state.mode === PLAYER_MODE.VIDEO_JINGLE ? { ...state, mode: PLAYER_MODE.PROGRAM_JINGLE } : state;
    case MANIFEST_LOADED: {
      if (!Number.isFinite(state.elapsedTimeInSeconds) && state.isLive) {
        return {
          ...state,
          elapsedTimeInSeconds:
            Math.round(Date.now() / 1000) - state.startTimeInSeconds - _get(data, 'payload.presentationDelay', 0),
        };
      }

      return state;
    }
    case SET_CURRENT_METADATA_ITEM: {
      const { item } = data;
      const startTimeInSeconds = _get(item, 'video.progress.live.startTimestamp', 0);
      const endTimeInSeconds = _get(item, 'video.progress.live.endTimestamp', 0);
      const durationInSeconds = endTimeInSeconds - startTimeInSeconds;
      const currentMetadataItemIndex = state.metadataItems.indexOf(item);
      const timeToLoadNewLayout = getTimeToRequestNewMetadataInSeconds(durationInSeconds);

      return {
        ...state,
        startTimeInSeconds,
        durationInSeconds,
        currentMetadataItemIndex,
        timeToLoadNewLayout,
      };
    }
    case APPEND_NEW_METADATA_ITEMS: {
      return {
        ...state,
        metadataItems: state.metadataItems.concat(
          state.shouldLiftParentalLock ? removeParentalLock(data.programs) : data.programs,
        ),
        shouldLiftParentalLock: false,
      };
    }
    case SIDE_GLASS_PAGE_REQUESTED: {
      const { page } = data;
      const block = state.sideGlass;

      if (!block) {
        return state;
      }

      const { items, pagination } = block.content;
      // NOTE: the placeholders let the panel be scrolled further while the page is being loaded
      const count = Math.min(
        pagination.totalItems - items.length,
        Math.max(page * pagination.itemsPerPage - items.length, 0) + pagination.itemsPerPage,
      );
      const placeholders = new Array(Math.max(count, 0)).fill(PLACEHOLDER);

      return {
        ...state,
        sideGlass: {
          ...block,
          content: { ...block.content, items: items.concat(placeholders) },
          pages: { ...block.pages, [page]: PAGE_STATE.LOADING },
        },
      };
    }
    case SIDE_GLASS_PAGE_LOADED: {
      const { page, items: loadedItems, totalItems } = data;
      const block = state.sideGlass;

      if (!block) {
        return state;
      }

      const { pagination } = block.content;
      const items = [...block.content.items];
      const start = page * pagination.itemsPerPage;

      if (start > items.length) {
        return state;
      }

      // NOTE: an empty page means the block holds less items than it was announced,
      // so the placeholders of the requested page are dropped
      if (!loadedItems.length) {
        return {
          ...state,
          sideGlass: {
            ...block,
            content: {
              ...block.content,
              items: items.slice(0, start),
              pagination: { ...pagination, totalItems: start },
            },
            pages: { ...block.pages, [page]: PAGE_STATE.LOADED },
          },
        };
      }

      for (let i = 0; i < loadedItems.length; i += 1) {
        items[start + i] = loadedItems[i];
      }

      return {
        ...state,
        sideGlass: {
          ...block,
          content: {
            ...block.content,
            items,
            pagination: { ...pagination, totalItems: totalItems || pagination.totalItems },
          },
          pages: { ...block.pages, [page]: PAGE_STATE.LOADED },
        },
      };
    }
    case SIDE_GLASS_PAGE_FAILED: {
      const { page } = data;
      const block = state.sideGlass;

      if (!block) {
        return state;
      }

      // NOTE: the page is marked as missing again, so it could be requested once more
      return {
        ...state,
        sideGlass: {
          ...block,
          pages: { ...block.pages, [page]: PAGE_STATE.MISSING },
        },
      };
    }
    case UPDATE_METADATA: {
      if (!data.payload) {
        return state;
      }

      const { currentMetadataItemIndex } = state;
      const currentItem = state.metadataItems[currentMetadataItemIndex];

      if (!currentItem) {
        return state;
      }

      const fieldsToUpdate = Object.keys(data.payload).filter(key => currentItem[key] !== data.payload[key]);
      if (!fieldsToUpdate.length) {
        return state;
      }

      return {
        ...state,
        metadataItems: state.metadataItems.map((value, index) =>
          index === currentMetadataItemIndex ? { ...value, ...data.payload } : value,
        ),
      };
    }
    case RESET:
      return {
        ...initialState,
        lastViewedClipId: state.lastViewedClipId,
        lastViewedClipProgress:
          state.elapsedTimeInSeconds > MIN_SECONDS_TO_TRACK_PROGRESS
            ? getPercentage(state.elapsedTimeInSeconds, state.durationInSeconds)
            : 0,
      };
    case RESET_LAST_VIEWED_VIDEO_DATA:
      return { ...state, lastViewedClipId: '', lastViewedClipProgress: 0 };
    case CAN_PLAY: {
      const duration = _get(data, 'payload.duration');

      return {
        ...state,
        canPlay: true,
        durationInSeconds: Number.isFinite(duration) ? Math.round(duration) : state.durationInSeconds,
        isLoadingContent: false,
      };
    }
    case CAN_PLAY_THROUGH: {
      const duration = _get(data, 'payload.duration');

      return {
        ...state,
        durationInSeconds: Number.isFinite(duration) ? Math.round(duration) : state.durationInSeconds,
      };
    }
    case PLAYING:
      return {
        ...state,
        timeUpdateTicks: 0,
        isBuffering: false,
        playbackState: PLAYBACK_STATE_PLAYING,
      };
    case PAUSED:
      return {
        ...state,
        playbackState: PLAYBACK_STATE_PAUSED,
      };
    case WAITING:
      return { ...state, isBuffering: data.payload.isBuffering };
    case TIME_UPDATE: {
      const { currentTime, liveEdge } = data.payload;
      const elapsedFromStart = Math.round(currentTime) - state.startTimeInSeconds;
      const elapsedTimeInSeconds =
        !state.isLive && elapsedFromStart > state.durationInSeconds ? state.elapsedTimeInSeconds : elapsedFromStart;
      const liveEdgeInSeconds = Number.isFinite(liveEdge) ? Math.round(liveEdge) - state.startTimeInSeconds : 0;

      const timeUpdateTicks = state.timeUpdateTicks + (state.playbackState === PLAYBACK_STATE_PLAYING ? 1 : 0);
      const shouldHideVideoLoader = state.shouldHideVideoLoader || timeUpdateTicks >= 2;

      const isContentSurface =
        state.mode === PLAYER_MODE.VIDEO_JINGLE ||
        state.mode === PLAYER_MODE.PROGRAM_JINGLE ||
        state.mode === PLAYER_MODE.AFTER_AD_JINGLE_IMAGE;
      const mode = isContentSurface && shouldHideVideoLoader ? PLAYER_MODE.CONTENT : state.mode;

      const chapter =
        state.isLive || mode !== PLAYER_MODE.CONTENT || state.playbackState !== PLAYBACK_STATE_PLAYING
          ? null
          : state.chapters.find(
              ({ tcStart, tcEnd }) => elapsedTimeInSeconds >= tcStart && elapsedTimeInSeconds <= tcEnd,
            );

      return {
        ...state,
        mode,
        isStarted: true,
        chapter,
        timeUpdateTicks,
        shouldHideVideoLoader,
        elapsedTimeInSeconds,
        liveEdgeInSeconds,
        isOnLive: Math.abs(liveEdgeInSeconds - elapsedTimeInSeconds) < 8,
      };
    }
    case TRACKS_CHANGED: {
      if (!data.payload) {
        return state;
      }

      const { selectedAudioTrack, selectedSubtitlesTrack, originalLanguage } = state;
      const subtitleTracks = (data.payload.subtitleTracks || []).map(parseTrack);
      const audioTracks = (data.payload.audioTracks || []).map(parseTrack);
      const preferences = state.preferences || {};

      const audioTrack =
        (selectedAudioTrack && audioTracks.find(({ id }) => id === selectedAudioTrack)) ||
        findMatchingAudioTrack(audioTracks, preferences.audioTrack, originalLanguage);

      const subtitlesTrack =
        (selectedSubtitlesTrack && subtitleTracks.find(({ id }) => id === selectedSubtitlesTrack)) ||
        findMatchingSubtitlesTrack(subtitleTracks, preferences.textTrack) ||
        (audioTrack ? findForcedSubtitles(subtitleTracks, audioTrack.language) : null);

      return {
        ...state,
        subtitleTracks,
        audioTracks,
        selectedSubtitlesTrack: subtitlesTrack ? subtitlesTrack.id : null,
        selectedAudioTrack: audioTrack ? audioTrack.id : null,
      };
    }
    case SET_AUDIO_TRACK: {
      return { ...state, selectedAudioTrack: data.track ? data.track.id : null };
    }
    case SET_SUBTITLE_TRACK: {
      return { ...state, selectedSubtitlesTrack: data.track ? data.track.id : null };
    }
    case TRACK_PREFERENCES_CHANGED: {
      const { audioTrack, subtitlesTrack } = data;
      const preferences = state.preferences || {};
      const { textTrack: currentTextTrack, audioTrack: currentAudioTrack } = preferences;

      let preferredAudioTrack = currentAudioTrack;
      if (audioTrack) {
        preferredAudioTrack = audioTrackToPreference(audioTrack);
      }

      let preferredTextTrack = currentTextTrack;
      if (subtitlesTrack === null) {
        preferredTextTrack = null;
      } else if (subtitlesTrack) {
        preferredTextTrack = subtitlesTrackToPreference(subtitlesTrack);
      }

      return {
        ...state,
        preferences: {
          ...preferences,
          audioTrack: preferredAudioTrack,
          textTrack: preferredTextTrack,
        },
      };
    }
    case SUBTITLES_UPDATED: {
      // Prevent subtitles flickering on the very app start
      if (state.timeUpdateTicks < 2) {
        return state;
      }

      return { ...state, subtitleCuePoint: data.payload };
    }
    case ENDED: {
      return {
        ...state,
        chapter: null,
        isBuffering: false,
        playbackState: PLAYBACK_STATE_ENDED,
      };
    }
    case AD_JINGLE_STARTED: {
      return {
        ...state,
        // Ad-engine-owned: the break is starting (fired before SLOT_START, ahead of any
        // jingle clip). Enter the "before ad" surface - it hides the main content and, more
        // importantly, is NOT the AD mode, so the countdown pill stays hidden until a real
        // ad actually plays. The before-ad image is shown if the layout provides one.
        mode: PLAYER_MODE.BEFORE_AD_JINGLE_IMAGE,
        selectedSubtitlesTrack: null,
        selectedAudioTrack: null,
      };
    }
    case AD_PROVIDER_LOADED: {
      return { ...state, isAdProviderReady: true };
    }
    case AD_MARKERS_LIST: {
      if (state.isLive) {
        return state;
      }

      return {
        ...state,
        adMarkers: (data.markers || []).filter(Boolean),
      };
    }
    case AD_ERROR: {
      return { ...state, isAdError: true };
    }
    case AD_BREAK_STARTED: {
      const { adType, totalDuration } = data;

      return {
        ...state,
        // Ad-engine-owned: never clobber an in-progress before-ad jingle (image or clip) -
        // doing so would briefly flip to AD and flash the countdown pill over the jingle.
        // The AD mode is reached from AD_PLAYING (a real ad). The fallback to AD here only
        // applies when no jingle preceded the break (ad-engine didn't fire the jingle event).
        mode:
          state.mode === PLAYER_MODE.BEFORE_AD_JINGLE_IMAGE || state.mode === PLAYER_MODE.BEFORE_AD_JINGLE_CLIP
            ? state.mode
            : PLAYER_MODE.AD,
        shouldHideVideoLoader: false,
        timeUpdateTicks: 0,
        adBreakElapsedTime: 0,
        adBreakDuration: totalDuration,
        displayedAdBreakType: adType,
        selectedSubtitlesTrack: null,
        selectedAudioTrack: null,
      };
    }
    case AD_BREAK_ENDED: {
      const isMidRoll = (state.displayedAdBreakType || '').startsWith(AD_BREAK_MID_ROLL);
      let mode = PLAYER_MODE.CONTENT;

      // NOTE: the content (re)loads behind a jingle surface, the TIME_UPDATE hands over to the content once it plays
      if (POST_AD_JINGLE_DELAY && isMidRoll) {
        mode = PLAYER_MODE.AFTER_AD_JINGLE_IMAGE;
      } else if (!state.isStarted) {
        mode = PLAYER_MODE.VIDEO_JINGLE;
      }

      return {
        ...state,
        mode,
        displayedAdBreakType: null,
      };
    }
    case AD_STARTED: {
      return {
        ...state,
        adSkipOffset: data.skipOffset,
      };
    }
    case AD_PLAYING: {
      return {
        ...state,
        // Ad-engine-owned: the first ad is now playing. Switching to the AD mode
        // also drops the before-ad jingle image, which used to be a separate flag,
        // it keeps the smooth, blink-free transition from the jingle to the ad.
        mode: PLAYER_MODE.AD,
        adPlaybackState: PLAYBACK_STATE_PLAYING,
        isAdError: false,
      };
    }
    case AD_JINGLE_CLIP_PLAYING: {
      return {
        ...state,
        // Ad-engine-owned: a jingle clip plays on the ads player. It shares the ads
        // surface/focus (isAdMode) but is NOT a real ad (mode !== AD), so no skip button
        // and no countdown pill (see isAdvertisementMode).
        mode:
          data.position === AD_JINGLE_POSITION.AFTER
            ? PLAYER_MODE.AFTER_AD_JINGLE_CLIP
            : PLAYER_MODE.BEFORE_AD_JINGLE_CLIP,
        adPlaybackState: PLAYBACK_STATE_PLAYING,
        adSkipOffset: undefined,
        isAdError: false,
      };
    }
    case AD_TIME_UPDATE: {
      return {
        ...state,
        adElapsedTime: data.currentTime,
        adBreakElapsedTime: data.breakTime,
      };
    }
    case AD_PAUSED: {
      return { ...state, adPlaybackState: PLAYBACK_STATE_PAUSED };
    }
    case AD_ENDED: {
      return { ...state, adPlaybackState: PLAYBACK_STATE_ENDED };
    }
    case SET_PAUSE_AD: {
      const { ad: pauseAd } = data;

      return { ...state, pauseAd, pauseAdState: PAUSE_AD_STATE.LOADING };
    }
    case SET_PAUSE_AD_STATE: {
      return { ...state, pauseAdState: data.state };
    }
    case SET_LBANNER_STATE: {
      return { ...state, lBannerState: data.state };
    }
    case SET_PLAYER_ERROR: {
      return { ...state, lastError: data.error };
    }
    case SWITCH_TO_NEXT_SOURCE: {
      return {
        ...state,
        currentSourceIndex: data.sourceIndex,
        mode: PLAYER_MODE.VIDEO_JINGLE,
        canPlay: false,
        isStarted: false,
        isLoadingContent: true,
        timeUpdateTicks: 0,
        shouldHideVideoLoader: false,
        lastError: null,
        isAdError: false,
      };
    }
    default:
      return state;
  }
};
